// 商品名からサイズを抽出するユーティリティ関数

export interface ProductSize {
  shortEdge: number; // 短辺（mm）
  longEdge: number;  // 長辺（mm）
  aspectRatio: number; // アスペクト比（長辺/短辺）
}

/**
 * 商品名からサイズ情報を抽出する
 * 例: "A5(148×210)" -> { shortEdge: 148, longEdge: 210, aspectRatio: 1.42 }
 * 例: "1000×1500" -> { shortEdge: 1000, longEdge: 1500, aspectRatio: 1.5 }
 * @param productTitle 商品名
 * @returns ProductSize | null
 */
export function parseProductSize(productTitle: string): ProductSize | null {
  // パターン1: 括弧内のサイズ "A5(148×210)"
  let sizeMatch = productTitle.match(/\((\d+(?:\.\d+)?)×(\d+(?:\.\d+)?)\)/);

  // パターン2: 括弧なしのサイズ "1000×1500"
  if (!sizeMatch) {
    sizeMatch = productTitle.match(/(\d+(?:\.\d+)?)×(\d+(?:\.\d+)?)/);
  }

  if (!sizeMatch) {
    console.log('サイズ情報が見つかりません:', productTitle);
    return null;
  }

  const [, shortEdgeStr, longEdgeStr] = sizeMatch;
  const shortEdge = parseFloat(shortEdgeStr);
  const longEdge = parseFloat(longEdgeStr);

  // 短辺と長辺を比較して正しい順序に調整
  const actualShortEdge = Math.min(shortEdge, longEdge);
  const actualLongEdge = Math.max(shortEdge, longEdge);
  const aspectRatio = actualLongEdge / actualShortEdge;

  console.log('サイズ抽出結果:', {
    original: productTitle,
    shortEdge: actualShortEdge,
    longEdge: actualLongEdge,
    aspectRatio: aspectRatio.toFixed(2)
  });

  return {
    shortEdge: actualShortEdge,
    longEdge: actualLongEdge,
    aspectRatio
  };
}

/**
 * 印刷領域の表示サイズを計算する（プレビュー用）
 * @param productSize 商品サイズ
 * @param maxWidth プレビュー領域の最大幅（px）
 * @param maxHeight プレビュー領域の最大高さ（px）
 * @returns { width: number, height: number }
 */
export function calculatePrintAreaSize(
  productSize: ProductSize,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  // アスペクト比を維持しながら、プレビュー領域に収まる最大サイズを計算
  const widthByHeight = maxHeight * productSize.aspectRatio;
  const heightByWidth = maxWidth / productSize.aspectRatio;

  let width: number;
  let height: number;

  if (widthByHeight <= maxWidth) {
    // 高さを基準に計算
    height = maxHeight;
    width = widthByHeight;
  } else {
    // 幅を基準に計算
    width = maxWidth;
    height = heightByWidth;
  }

  return { width, height };
}

/**
 * 指定された寸法に収まる最小のバリエーションを検索する
 * @param width 幅（mm）
 * @param height 高さ（mm）
 * @param variants 商品バリエーションの配列
 * @returns 最適なバリエーション、またはnull
 */
export function findSmallestFittingVariant(
  width: number,
  height: number,
  variants: { id: number; title: string }[]
): { id: number; title: string } | null {
  // バリエーションをサイズ順（面積順）にソート
  const variantsWithSizes = variants.map(variant => {
    const size = parseProductSize(variant.title);
    return {
      variant,
      size,
      area: size ? size.shortEdge * size.longEdge : Infinity
    };
  }).filter(item => item.size !== null)
    .sort((a, b) => a.area - b.area);

  // 最小の適合するサイズを探す
  const fittingItem = variantsWithSizes.find(({ size }) => {
    if (!size) return false;
    // 通常の向きで収まるか
    const fitsNormal = size.shortEdge >= Math.min(width, height) &&
      size.longEdge >= Math.max(width, height);
    // 回転して収まるか（用紙サイズは通常固定だが、念のため）
    // ※用紙サイズは「短辺x長辺」で定義されているため、入力が「長辺x短辺」でもマッチするように
    //   Math.min/maxで比較しています。

    return fitsNormal;
  });

  return fittingItem ? fittingItem.variant : null;
}
